import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function RewiringPage() {
  useEffect(() => {
    document.title = 'Full & Partial Rewiring Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert house rewiring services in Watford. Full and partial rewiring by NICEIC certified electricians. BS7671 compliant. Call +44 7989 675952 for a free quote.');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/rewiring`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'rewiring-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/rewiring`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "postalCode": "WD17",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££"
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "House Rewiring Watford",
        "description": "Professional full and partial house rewiring services in Watford. NICEIC certified electricians upgrading outdated electrical systems to modern safety standards.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "House Rewiring",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/installations/rewiring`
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('rewiring-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/d379f1a8c5eab8209a847747db87f4a6.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Certified
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford Specialists
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Full &amp; Partial Rewiring<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional house rewiring services for homes in Watford, Rickmansworth, and surrounding areas. Upgrade your outdated electrical system to modern safety standards with minimal disruption.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Rewiring Services in Watford
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete and partial rewiring solutions for residential properties throughout Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏠
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Full House Rewiring</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Complete rewiring of your entire Watford property, replacing all outdated cables, sockets, switches, and consumer units to meet current regulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔌
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Partial Rewiring</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Targeted rewiring of specific rooms or circuits in your Watford home, ideal for kitchen or bathroom renovations and extensions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔧
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Consumer Unit Replacement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Modern consumer unit installation with RCD protection as part of your rewiring project in Watford, ensuring maximum electrical safety.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  ⚡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Circuit Upgrades</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Upgrade individual circuits to accommodate modern electrical demands in your Watford home, including dedicated appliance circuits.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🛡️
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Safety Testing</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Comprehensive electrical testing and certification upon completion of all rewiring work in Watford, with full NICEIC documentation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🎨
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Minimal Disruption</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Professional rewiring with careful planning to minimize disruption to your daily life in your Watford home, with tidy workmanship.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signs You Need Rewiring */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Does Your Watford Home Need Rewiring?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Common signs that indicate your property requires electrical rewiring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">⚠️</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Old Wiring</h3>
              <p className="text-gray-600 text-sm">
                Property over 25-30 years old with original wiring in Watford
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🔌</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Frequent Tripping</h3>
              <p className="text-gray-600 text-sm">
                Circuit breakers trip regularly or fuses blow often
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">💡</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Flickering Lights</h3>
              <p className="text-gray-600 text-sm">
                Lights dim or flicker when appliances are used
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🔥</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Burning Smell</h3>
              <p className="text-gray-600 text-sm">
                Unusual burning odors from sockets or switches
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">🔲</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Old Fuse Box</h3>
              <p className="text-gray-600 text-sm">
                Still using an old-style fuse box instead of modern consumer unit
              </p>
            </div>

            <div className="bg-white border-2 border-[#FFD700]/30 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Electric Shocks</h3>
              <p className="text-gray-600 text-sm">
                Mild shocks when touching appliances or switches
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of Rewiring Your Watford Home
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why invest in professional rewiring services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Enhanced Safety</h3>
              <p className="text-gray-600 leading-relaxed">
                Eliminate electrical hazards and reduce fire risk with modern wiring that meets current safety standards in Watford homes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Increased Property Value</h3>
              <p className="text-gray-600 leading-relaxed">
                Boost your Watford property value with a modern electrical system and full certification for potential buyers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Modern Capacity</h3>
              <p className="text-gray-600 leading-relaxed">
                Support modern appliances and technology with adequate electrical capacity throughout your Watford home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical for Rewiring?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Trusted rewiring specialists serving Watford for over 15 years
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                Rewiring Watford homes since 2009
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">NICEIC Certified</h3>
              <p className="text-gray-300 text-sm">
                Full certification provided
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🎨</div>
              <h3 className="text-xl font-bold text-white mb-2">Tidy Workmanship</h3>
              <p className="text-gray-300 text-sm">
                Minimal disruption guaranteed
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">Fixed Pricing</h3>
              <p className="text-gray-300 text-sm">
                No hidden costs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Areas We Cover
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional rewiring services across Watford and surrounding areas
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/services/installations/rewiring/rickmansworth"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Rickmansworth
            </Link>
            <Link
              to="/services/installations/rewiring/chorleywood"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Chorleywood
            </Link>
            <Link
              to="/services/installations/rewiring/moor-park-northwood"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Moor Park & Northwood
            </Link>
            <Link
              to="/services/installations/rewiring/croxley-green"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Croxley Green
            </Link>
            <Link
              to="/services/installations/rewiring/pinner"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Pinner
            </Link>
            <Link
              to="/services/installations/rewiring/bushey"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Bushey
            </Link>
            <Link
              to="/services/installations/rewiring/abbots-langley"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Abbots Langley
            </Link>
            <Link
              to="/services/installations/rewiring/harrow"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Harrow
            </Link>
            <Link
              to="/services/installations/rewiring/ruislip"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Ruislip
            </Link>
            <Link
              to="/services/installations/rewiring/stanmore"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Stanmore
            </Link>
            <Link
              to="/services/installations/rewiring/edgware"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Edgware
            </Link>
            <Link
              to="/services/installations/rewiring/mill-hill"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Mill Hill
            </Link>
            <Link
              to="/services/installations/rewiring/radlett"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Radlett
            </Link>
            <Link
              to="/services/installations/rewiring/borehamwood"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Borehamwood
            </Link>
            <Link
              to="/services/installations/rewiring/elstree"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Elstree
            </Link>
            <Link
              to="/services/installations/rewiring/potters-bar"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              Potters Bar
            </Link>
            <Link
              to="/services/installations/rewiring/south-oxhey"
              className="px-6 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 rounded-full font-medium hover:from-amber-500 hover:to-yellow-600 transition-all duration-300 whitespace-nowrap"
            >
              South Oxhey
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need House Rewiring in Watford?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us today for a free rewiring assessment and quote. Our experienced electricians will survey your Watford property and provide expert advice.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}